import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSiteContent } from '@/hooks/useSiteContent';

/**
 * Centralized SEO component. Sets title, description, canonical, OG and (optionally)
 * Google Search Console verification + LocalBusiness JSON-LD on selected pages.
 */
export default function SEO({
  title,
  description,
  path = '',
  image,
  type = 'website',
  schema, // optional extra JSON-LD object
}) {
  const { content } = useSiteContent();
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const url = `${origin}${path}`;
  const fullTitle = title ? `${title} | Dubinko Niš` : 'Dubinko — Dubinsko čišćenje u Nišu';
  const desc = description || 'Premium dubinsko čišćenje u Nišu — stanovi, poslovni prostori, tepisi i tapacirani nameštaj. Zakažite termin online.';
  const og = image || 'https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?auto=format&fit=crop&w=1200&q=80';

  // Inject GA gtag only if configured (defers loading to client)
  useEffect(() => {
    if (!content.ga_id || typeof window === 'undefined') return;
    if (document.getElementById('ga-loader')) return;
    const s = document.createElement('script');
    s.id = 'ga-loader';
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${content.ga_id}`;
    document.head.appendChild(s);

    const i = document.createElement('script');
    i.id = 'ga-init';
    i.innerHTML = `window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', '${content.ga_id}');`;
    document.head.appendChild(i);
  }, [content.ga_id]);

  // Google Ads (separate config call so the gtag conversions work independently)
  useEffect(() => {
    if (!content.google_ads_conversion_id || typeof window === 'undefined') return;
    if (document.getElementById('gads-init')) return;
    if (!content.ga_id && !document.getElementById('ga-loader')) {
      const s = document.createElement('script');
      s.id = 'gads-loader';
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${content.google_ads_conversion_id}`;
      document.head.appendChild(s);
    }
    const i = document.createElement('script');
    i.id = 'gads-init';
    i.innerHTML = `window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', '${content.google_ads_conversion_id}');`;
    document.head.appendChild(i);
  }, [content.google_ads_conversion_id, content.ga_id]);

  // Facebook Pixel
  useEffect(() => {
    if (!content.facebook_pixel_id || typeof window === 'undefined') return;
    if (document.getElementById('fbq-init')) return;
    const s = document.createElement('script');
    s.id = 'fbq-init';
    s.innerHTML = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init', '${content.facebook_pixel_id}');fbq('track', 'PageView');`;
    document.head.appendChild(s);
  }, [content.facebook_pixel_id]);

  return (
    <Helmet>
      <html lang="sr-Latn" />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {content.gsc_verification && (
        <meta name="google-site-verification" content={content.gsc_verification} />
      )}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={og} />
      <meta property="og:locale" content="sr_RS" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={og} />
      {schema && (Array.isArray(schema) ? schema : [schema]).filter(Boolean).map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
}

export function buildLocalBusinessSchema(content, reviewsAggregate) {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CleaningService',
    name: 'Dubinko',
    description: 'Premium dubinsko čišćenje stanova, kuća, poslovnih prostora, tepiha i tapaciranog nameštaja u Nišu.',
    url: origin,
    telephone: content.phone,
    email: content.email,
    image: 'https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?auto=format&fit=crop&w=1200&q=80',
    priceRange: '€€',
    address: {
      '@type': 'PostalAddress',
      streetAddress: content.business_street,
      postalCode: content.business_postal,
      addressLocality: content.business_city,
      addressCountry: content.business_country || 'RS',
    },
    geo: content.business_lat && content.business_lng ? {
      '@type': 'GeoCoordinates',
      latitude: content.business_lat,
      longitude: content.business_lng,
    } : undefined,
    areaServed: [
      'Niš', 'Niška Banja', 'Medijana', 'Palilula', 'Pantelej', 'Crveni Krst',
    ].map((n) => ({ '@type': 'City', name: n })),
    sameAs: [content.instagram, content.gbp_url].filter(Boolean),
    openingHours: content.opening_hours,
  };
  if (reviewsAggregate?.count && reviewsAggregate.count > 0) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: reviewsAggregate.avg.toFixed(1),
      reviewCount: reviewsAggregate.count,
    };
  }
  return schema;
}
